import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";

import {
  Visibility,
  VisibilityOff,
  LockOutlined,
  PersonOutline,
  EmailOutlined,
  BadgeOutlined,
  PhoneOutlined,
  LocationOnOutlined,
  CalendarTodayOutlined,
  SecurityOutlined,
} from "@mui/icons-material";

import ApiConfig from "../../Config";
import profileService from "../../repository/profileRepository";

const Profile = () => {
  const [profile, setProfile] = useState(null);

  const [loading, setLoading] = useState(true);
  const [changingPassword, setChangingPassword] = useState(false);

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showMessage = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const token = sessionStorage.getItem("newToken");
      const adminId = sessionStorage.getItem("adminId");

      if (!token || !adminId) {
        showMessage("Your session has expired. Please login again.", "error");

        sessionStorage.clear();
        return;
      }

      //   const response = await axios.get(
      //     `${ApiConfig.apiBaseUrl}${ApiConfig.getAdminById}`,
      //     {
      //       headers: {
      //         "x-access-token": token,
      //         "admin-id": adminId,
      //       },
      //     },
      //   );
      const response = await profileService.getProfile(token, adminId);
      console.log("Profile response:", response); // Log the response for debugging

      // API returns { adminData: {...} }
      setProfile(response.data.adminData);
    } catch (error) {
      console.error("Profile error:", error);

      showMessage(
        error.response?.data?.message || "Unable to load profile.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;

    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const changePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordData;

    if (!currentPassword || !newPassword || !confirmPassword) {
      showMessage("Please fill all password fields.", "error");
      return;
    }

    if (newPassword.length < 8) {
      showMessage("New password must be at least 8 characters.", "error");
      return;
    }

    if (newPassword !== confirmPassword) {
      showMessage("New password and confirm password do not match.", "error");
      return;
    }

    try {
      setChangingPassword(true);

      const token = sessionStorage.getItem("newToken");
      const adminId = sessionStorage.getItem("adminId");

      if (!token || !adminId) {
        showMessage("Your session has expired. Please login again.", "error");

        sessionStorage.clear();
        return;
      }
      await axios.post(
        `${ApiConfig.apiBaseUrl}${ApiConfig.resetPassword}`,
        {
          oldPassword: currentPassword,
          newPassword: newPassword,
        },
        {
          headers: {
            "x-access-token": token,
            "admin-id": adminId,
          },
        },
      );

      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      showMessage("Password changed successfully.", "success");
    } catch (error) {
      console.error("Change password error:", error);

      showMessage(
        error.response?.data?.message || "Unable to change password.",
        "error",
      );
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography>Loading profile...</Typography>
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Unable to load profile.</Alert>
      </Box>
    );
  }

  const role = String(profile.role || "").toUpperCase();

  return (
    <Box
      sx={{
        p: {
          xs: 2,
          md: 4,
        },
        maxWidth: 1200,
        mx: "auto",
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          sx={{
            fontSize: 12,
            letterSpacing: "2px",
            textTransform: "uppercase",
            color: "#806B52",
            fontWeight: 700,
            mb: 1,
          }}
        >
          Account
        </Typography>

        <Typography
          sx={{
            fontSize: {
              xs: 30,
              md: 38,
            },
            fontWeight: 400,
            fontFamily: "Georgia, 'Times New Roman', serif",
          }}
        >
          My Profile
        </Typography>

        <Typography
          sx={{
            mt: 1,
            color: "text.secondary",
            fontSize: 14,
          }}
        >
          Manage your account information and security settings.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Profile information */}
        <Grid item xs={12} md={7}>
          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                }}
              >
                <Avatar
                  sx={{
                    width: 68,
                    height: 68,
                    backgroundColor: "#806B52",
                    fontSize: 24,
                  }}
                >
                  {(profile.name || "A").charAt(0).toUpperCase()}
                </Avatar>

                <Box>
                  <Typography
                    sx={{
                      fontSize: 20,
                      fontWeight: 600,
                    }}
                  >
                    {profile.name || "-"}
                  </Typography>

                  <Typography
                    sx={{
                      color: "text.secondary",
                      fontSize: 13,
                      mt: 0.5,
                    }}
                  >
                    {role}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <ProfileField
                    icon={<PersonOutline />}
                    label="Full Name"
                    value={profile.name}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <ProfileField
                    icon={<EmailOutlined />}
                    label="Email"
                    value={profile.email}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <ProfileField
                    icon={<BadgeOutlined />}
                    label="Admin ID"
                    value={profile.adminId}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <ProfileField
                    icon={<SecurityOutlined />}
                    label="Role"
                    value={role}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <ProfileField
                    icon={<PhoneOutlined />}
                    label="Contact"
                    value={profile.contact}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <ProfileField
                    icon={<CalendarTodayOutlined />}
                    label="Date of Birth"
                    value={profile.admin_DOB}
                  />
                </Grid>

                <Grid item xs={12}>
                  <ProfileField
                    icon={<LocationOnOutlined />}
                    label="Address"
                    value={[
                      profile.address?.district,
                      profile.address?.city,
                      profile.address?.state,
                      profile.address?.country,
                      profile.address?.pinCode,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  />
                </Grid>

                <Grid item xs={12}>
                  <ProfileField
                    icon={<SecurityOutlined />}
                    label="Two-Factor Authentication"
                    value={
                      profile["2FA"] === "true" || profile.set2FA === "true"
                        ? "Enabled"
                        : "Disabled"
                    }
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Change password */}
        <Grid item xs={12} md={5}>
          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 1,
                }}
              >
                <LockOutlined
                  sx={{
                    color: "#806B52",
                  }}
                />

                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 600,
                  }}
                >
                  Change Password
                </Typography>
              </Box>

              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: 13,
                  mb: 3,
                }}
              >
                Update your password to keep your account secure.
              </Typography>

              <PasswordField
                label="Current Password"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                visible={showCurrent}
                onToggle={() => setShowCurrent((prev) => !prev)}
              />

              <PasswordField
                label="New Password"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                visible={showNew}
                onToggle={() => setShowNew((prev) => !prev)}
              />

              <PasswordField
                label="Confirm New Password"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                visible={showConfirm}
                onToggle={() => setShowConfirm((prev) => !prev)}
              />

              <Button
                fullWidth
                variant="contained"
                onClick={changePassword}
                disabled={changingPassword}
                sx={{
                  mt: 1,
                  height: 50,
                  backgroundColor: "#292621",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#171512",
                  },
                }}
              >
                {changingPassword ? "Changing Password..." : "Change Password"}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() =>
          setSnackbar((prev) => ({
            ...prev,
            open: false,
          }))
        }
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() =>
            setSnackbar((prev) => ({
              ...prev,
              open: false,
            }))
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const ProfileField = ({ icon, label, value }) => {
  return (
    <Box
      sx={{
        p: 1.8,
        borderRadius: 1.5,
        backgroundColor: "action.hover",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 0.7,
        }}
      >
        {React.cloneElement(icon, {
          sx: {
            fontSize: 17,
            color: "#806B52",
          },
        })}

        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            color: "text.secondary",
          }}
        >
          {label}
        </Typography>
      </Box>

      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 500,
          wordBreak: "break-word",
        }}
      >
        {value || "-"}
      </Typography>
    </Box>
  );
};

const PasswordField = ({ label, name, value, onChange, visible, onToggle }) => {
  return (
    <TextField
      fullWidth
      label={label}
      name={name}
      type={visible ? "text" : "password"}
      value={value}
      onChange={onChange}
      sx={{ mb: 2 }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={onToggle} edge="end">
              {visible ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

export default Profile;
